<template>
    <div class="reservation-record">
        <div class="page-header">
            <div>
                <h2 class="page-title">预约记录</h2>
                <p class="page-desc">管理所有预约订单，支持筛选和导出</p>
            </div>
            <div class="header-actions">
                <el-button type="success" @click="exportRecords">
                    <el-icon><Download /></el-icon> 导出CSV
                </el-button>
            </div>
        </div>

        <!-- 统计卡片 -->
        <div class="stat-cards">
            <div class="stat-card" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
                <div class="stat-value">{{ stats.total || 0 }}</div>
                <div class="stat-label">总预约数</div>
            </div>
            <div class="stat-card" style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);">
                <div class="stat-value">{{ stats.pending || 0 }}</div>
                <div class="stat-label">待处理</div>
            </div>
            <div class="stat-card" style="background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);">
                <div class="stat-value">{{ stats.confirmed || 0 }}</div>
                <div class="stat-label">已确认</div>
            </div>
            <div class="stat-card" style="background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);">
                <div class="stat-value">{{ stats.completed || 0 }}</div>
                <div class="stat-label">已完成</div>
            </div>
        </div>

        <!-- 筛选栏 - 优化布局，使用 flex-wrap 并设置合适的间距 -->
        <div class="filter-bar">
            <el-select v-model="filters.product_id" placeholder="请选择商品" clearable filterable style="width: 180px"
                @change="fetchData">
                <el-option v-for="product in productOptions" :key="product.id" :label="product.product_name"
                    :value="product.id" />
            </el-select>
            <el-select v-model="filters.branch_id" placeholder="请选择门店" clearable filterable style="width: 150px"
                @change="onBranchChange">
                <el-option v-for="store in storeOptions" :key="store.id" :label="store.store_name" :value="store.id" />
            </el-select>
            <el-select v-model="filters.store_branch_id" placeholder="请选择分店" clearable filterable style="width: 150px"
                :disabled="!filters.branch_id" @change="fetchData">
                <el-option v-for="branch in branchOptions" :key="branch.id" :label="branch.branch_name"
                    :value="branch.id" />
            </el-select>
            <el-select v-model="filters.status" placeholder="全部状态" clearable style="width: 110px" @change="fetchData">
                <el-option label="待处理" :value="0" />
                <el-option label="已确认" :value="1" />
                <el-option label="已完成" :value="2" />
                <el-option label="已取消" :value="3" />
                <el-option label="已过期" :value="4" />
            </el-select>
            <el-date-picker v-model="dateRange" type="daterange" range-separator="至" start-placeholder="开始"
                end-placeholder="结束" value-format="YYYY-MM-DD" style="width: 240px" @change="onDateChange" />
            <el-input v-model="searchKey" placeholder="搜索用户/手机/商品" prefix-icon="Search" clearable style="width: 180px"
                @keyup.enter="fetchData" />
            <el-button type="primary" @click="fetchData">查询</el-button>
            <el-button @click="resetSearch">重置</el-button>
        </div>

        <!-- 预约记录表格 -->
        <el-table :data="recordList" border v-loading="loading" style="width: 100%">
            <el-table-column label="预约信息" min-width="200">
                <template #default="{ row }">
                    <div class="record-info">
                        <div class="product-name">{{ row.product_name }}</div>
                        <div class="branch-name" v-if="row.branch_name">{{ row.branch_name }}</div>
                        <div class="reservation-time">预约时间: {{ row.reservation_time }}</div>
                    </div>
                </template>
            </el-table-column>
            <el-table-column label="用户信息" min-width="150">
                <template #default="{ row }">
                    <div class="user-info">
                        <div>{{ row.user_nickname || '匿名用户' }}</div>
                        <div class="user-phone">{{ row.user_phone || '未填写' }}</div>
                    </div>
                </template>
            </el-table-column>
            <el-table-column label="预约详情" min-width="150">
                <template #default="{ row }">
                    <div>人数: {{ row.people_count }}人</div>
                    <div v-if="row.reservation_slot">时段: {{ row.reservation_slot }}</div>
                    <div class="remark" v-if="row.remark">备注: {{ row.remark }}</div>
                </template>
            </el-table-column>
            <el-table-column label="状态" width="90" align="center">
                <template #default="{ row }">
                    <el-tag :type="getStatusType(row.status)" size="small">
                        {{ getStatusText(row.status) }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column label="创建时间" width="155" align="center">
                <template #default="{ row }">
                    {{ row.create_time }}
                </template>
            </el-table-column>
            <el-table-column label="操作" width="220" fixed="right" align="center">
                <template #default="{ row }">
                    <div class="action-buttons">
                        <el-button type="primary" link size="small" @click="viewDetail(row)">
                            <el-icon><View /></el-icon> 详情
                        </el-button>
                        <el-dropdown v-if="row.status === 0 || row.status === 1" trigger="click" @command="(cmd) => handleStatusChange(row, cmd)">
                            <el-button type="warning" link size="small" class="process-btn">
                                <el-icon><Setting /></el-icon> 处理
                                <el-icon class="el-icon--right"><ArrowDown /></el-icon>
                            </el-button>
                            <template #dropdown>
                                <el-dropdown-menu>
                                    <el-dropdown-item command="confirm" v-if="row.status === 0">
                                        <el-icon><CircleCheck /></el-icon> 确认预约
                                    </el-dropdown-item>
                                    <el-dropdown-item command="complete" v-if="row.status === 1">
                                        <el-icon><SuccessFilled /></el-icon> 标记完成
                                    </el-dropdown-item>
                                    <el-dropdown-item command="cancel" divided>
                                        <el-icon><CircleClose /></el-icon> 取消预约
                                    </el-dropdown-item>
                                </el-dropdown-menu>
                            </template>
                        </el-dropdown>
                        <el-popconfirm title="确定删除该预约记录吗？" @confirm="handleDelete(row.id)">
                            <template #reference>
                                <el-button type="danger" link size="small">
                                    <el-icon><Delete /></el-icon> 删除
                                </el-button>
                            </template>
                        </el-popconfirm>
                    </div>
                </template>
            </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-wrapper">
            <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
                :total="pagination.total" :page-sizes="[10, 20, 50, 100]"
                layout="total, sizes, prev, pager, next, jumper" @size-change="fetchData" @current-change="fetchData" />
        </div>

        <!-- 详情弹窗 -->
        <el-dialog v-model="detailVisible" title="预约详情" width="650px">
            <div class="detail-content" v-if="currentRecord">
                <div class="detail-section">
                    <h4>基本信息</h4>
                    <el-descriptions :column="2" border>
                        <el-descriptions-item label="商品名称">{{ currentRecord.product_name }}</el-descriptions-item>
                        <el-descriptions-item label="门店/分店">{{ currentRecord.branch_name || '无' }}</el-descriptions-item>
                        <el-descriptions-item label="预约时间">{{ currentRecord.reservation_time }}</el-descriptions-item>
                        <el-descriptions-item label="预约人数">{{ currentRecord.people_count }}人</el-descriptions-item>
                        <el-descriptions-item label="预约时段">{{ currentRecord.reservation_slot || '无' }}</el-descriptions-item>
                        <el-descriptions-item label="状态">{{ getStatusText(currentRecord.status) }}</el-descriptions-item>
                        <el-descriptions-item label="创建时间">{{ currentRecord.create_time }}</el-descriptions-item>
                        <el-descriptions-item label="备注">{{ currentRecord.remark || '无' }}</el-descriptions-item>
                    </el-descriptions>
                </div>
                <div class="detail-section" v-if="currentRecord.form_data">
                    <h4>表单数据</h4>
                    <el-descriptions :column="1" border>
                        <el-descriptions-item v-for="(value, key) in currentRecord.form_data" :key="key" :label="key">
                            {{ typeof value === 'object' ? JSON.stringify(value) : value }}
                        </el-descriptions-item>
                    </el-descriptions>
                </div>
                <div class="detail-section" v-if="currentRecord.admin_remark">
                    <h4>管理员备注</h4>
                    <div class="admin-remark">{{ currentRecord.admin_remark }}</div>
                </div>
            </div>
            <template #footer>
                <el-button @click="detailVisible = false">关 闭</el-button>
            </template>
        </el-dialog>

        <!-- 处理弹窗 -->
        <el-dialog v-model="processVisible" :title="processTitle" width="450px">
            <el-form :model="processForm" label-width="80px">
                <el-form-item label="备注">
                    <el-input v-model="processForm.remark" type="textarea" :rows="3" placeholder="请输入处理备注" />
                </el-form-item>
                <el-form-item label="取消原因" v-if="processAction === 'cancel'">
                    <el-input v-model="processForm.cancel_reason" placeholder="请输入取消原因" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="processVisible = false">取消</el-button>
                <el-button type="primary" @click="submitProcess" :loading="processLoading">确 定</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { Download, View, Delete, ArrowDown, Setting, CircleCheck, SuccessFilled, CircleClose } from '@element-plus/icons-vue'

export default {
    name: 'ReservationRecordPage',
    components: { Download, View, Delete, ArrowDown, Setting, CircleCheck, SuccessFilled, CircleClose },
    setup() {
        const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
        const agentId = ref(userInfo.agent_id || 0)
        const storeId = ref(userInfo.id)

        const loading = ref(false)
        const processLoading = ref(false)
        const recordList = ref([])
        const productOptions = ref([])
        const storeOptions = ref([])
        const branchOptions = ref([])
        const searchKey = ref('')
        const dateRange = ref([])
        const detailVisible = ref(false)
        const processVisible = ref(false)
        const currentRecord = ref(null)
        const processAction = ref('')
        const processTitle = ref('')

        const filters = reactive({
            product_id: null,
            branch_id: null,
            store_branch_id: null,
            status: null
        })

        const pagination = reactive({
            page: 1,
            limit: 20,
            total: 0
        })

        const stats = reactive({
            total: 0,
            pending: 0,
            confirmed: 0,
            completed: 0,
            cancelled: 0,
            expired: 0
        })

        const processForm = reactive({
            remark: '',
            cancel_reason: ''
        })

        const getStatusText = (status) => {
            const map = { 0: '待处理', 1: '已确认', 2: '已完成', 3: '已取消', 4: '已过期' }
            return map[status] || '未知'
        }

        const getStatusType = (status) => {
            const map = { 0: 'warning', 1: 'primary', 2: 'success', 3: 'danger', 4: 'info' }
            return map[status] || 'info'
        }

        // 获取商品列表
        const fetchProducts = async () => {
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/product_api.php`, {
                    params: { agent_id: agentId.value, store_id: storeId.value, limit: 100, product_type: 2 }
                })
                if (response.data.code === 200) {
                    productOptions.value = response.data.data.list
                }
            } catch (error) {
                console.error('获取商品失败:', error)
            }
        }

        // 获取门店列表
        const fetchStores = async () => {
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/group_store_api.php`, {
                    params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
                })
                if (response.data.code === 200) {
                    storeOptions.value = response.data.data.list
                }
            } catch (error) {
                console.error('获取门店失败:', error)
            }
        }

        // 获取分店列表
        const fetchBranches = async (branchId) => {
            if (!branchId) {
                branchOptions.value = []
                return
            }
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/store_branch_api.php`, {
                    params: { agent_id: agentId.value, store_id: storeId.value, branch_id: branchId, limit: 100 }
                })
                if (response.data.code === 200) {
                    branchOptions.value = response.data.data.list
                }
            } catch (error) {
                console.error('获取分店失败:', error)
            }
        }

        const onBranchChange = () => {
            filters.store_branch_id = null
            fetchBranches(filters.branch_id)
            fetchData()
        }

        const onDateChange = () => {
            fetchData()
        }

        // 获取预约记录
        const fetchData = async () => {
            loading.value = true
            try {
                const params = {
                    agent_id: agentId.value,
                    store_id: storeId.value,
                    page: pagination.page,
                    limit: pagination.limit,
                    search: searchKey.value,
                    product_id: filters.product_id,
                    branch_id: filters.branch_id,
                    store_branch_id: filters.store_branch_id,
                    status: filters.status
                }
                if (dateRange.value && dateRange.value.length === 2) {
                    params.start_date = dateRange.value[0]
                    params.end_date = dateRange.value[1]
                }
                const response = await axios.get(`${config.BASE_URL}/shop/api/reservation_record_api.php`, { params })
                if (response.data.code === 200) {
                    recordList.value = response.data.data.list
                    pagination.total = response.data.data.pagination.total
                    if (response.data.data.stats) {
                        Object.assign(stats, response.data.data.stats)
                    }
                }
            } catch (error) {
                console.error('获取预约记录失败:', error)
                ElMessage.error('获取数据失败')
            } finally {
                loading.value = false
            }
        }

        const resetSearch = () => {
            searchKey.value = ''
            dateRange.value = []
            filters.product_id = null
            filters.branch_id = null
            filters.store_branch_id = null
            filters.status = null
            pagination.page = 1
            fetchData()
        }

        const viewDetail = (row) => {
            currentRecord.value = row
            detailVisible.value = true
        }

        const handleStatusChange = (row, action) => {
            currentRecord.value = row
            processAction.value = action
            processForm.remark = ''
            processForm.cancel_reason = ''
            if (action === 'confirm') {
                processTitle.value = '确认预约'
            } else if (action === 'complete') {
                processTitle.value = '标记完成'
            } else if (action === 'cancel') {
                processTitle.value = '取消预约'
            }
            processVisible.value = true
        }

        const submitProcess = async () => {
            processLoading.value = true
            const loadingInstance = ElLoading.service({ text: '处理中...' })
            try {
                const payload = {
                    id: currentRecord.value.id,
                    store_id: storeId.value,
                    admin_remark: processForm.remark
                }
                if (processAction.value === 'confirm') {
                    payload.status = 1
                    payload.confirm_time = new Date().toISOString().slice(0, 19).replace('T', ' ')
                } else if (processAction.value === 'complete') {
                    payload.status = 2
                    payload.complete_time = new Date().toISOString().slice(0, 19).replace('T', ' ')
                } else if (processAction.value === 'cancel') {
                    payload.status = 3
                    payload.cancel_time = new Date().toISOString().slice(0, 19).replace('T', ' ')
                    payload.cancel_reason = processForm.cancel_reason
                }
                const response = await axios.put(`${config.BASE_URL}/shop/api/reservation_record_api.php`, payload)
                if (response.data.code === 200) {
                    ElMessage.success('操作成功')
                    processVisible.value = false
                    fetchData()
                } else {
                    ElMessage.error(response.data.message || '操作失败')
                }
            } catch (error) {
                console.error('操作失败:', error)
                ElMessage.error('操作失败')
            } finally {
                processLoading.value = false
                loadingInstance.close()
            }
        }

        const handleDelete = async (id) => {
            const loadingInstance = ElLoading.service({ text: '删除中...' })
            try {
                const response = await axios.delete(`${config.BASE_URL}/shop/api/reservation_record_api.php`, {
                    data: { id, store_id: storeId.value }
                })
                if (response.data.code === 200) {
                    ElMessage.success('删除成功')
                    fetchData()
                } else {
                    ElMessage.error(response.data.message || '删除失败')
                }
            } catch (error) {
                console.error('删除失败:', error)
                ElMessage.error('删除失败')
            } finally {
                loadingInstance.close()
            }
        }

        // 导出CSV
        const exportRecords = () => {
            let url = `${config.BASE_URL}/shop/api/reservation_record_api.php?export=1&agent_id=${agentId.value}&store_id=${storeId.value}`
            
            if (filters.product_id) url += `&product_id=${filters.product_id}`
            if (filters.branch_id) url += `&branch_id=${filters.branch_id}`
            if (filters.store_branch_id) url += `&store_branch_id=${filters.store_branch_id}`
            if (filters.status !== null && filters.status !== '') url += `&status=${filters.status}`
            if (searchKey.value) url += `&search=${encodeURIComponent(searchKey.value)}`
            if (dateRange.value && dateRange.value.length === 2) {
                url += `&start_date=${dateRange.value[0]}&end_date=${dateRange.value[1]}`
            }
            
            window.open(url, '_blank')
        }

        onMounted(() => {
            fetchProducts()
            fetchStores()
            fetchData()
        })

        return {
            loading,
            processLoading,
            recordList,
            productOptions,
            storeOptions,
            branchOptions,
            searchKey,
            dateRange,
            detailVisible,
            processVisible,
            currentRecord,
            filters,
            pagination,
            stats,
            processForm,
            getStatusText,
            getStatusType,
            onBranchChange,
            onDateChange,
            fetchData,
            resetSearch,
            viewDetail,
            handleStatusChange,
            submitProcess,
            handleDelete,
            exportRecords
        }
    }
}
</script>

<style scoped lang="scss">
.reservation-record {
    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;

        .page-title {
            font-size: 24px;
            font-weight: 600;
            color: #1a1a2e;
            margin-bottom: 4px;
        }

        .page-desc {
            font-size: 14px;
            color: #8c8c8c;
        }

        .header-actions {
            display: flex;
            gap: 12px;
        }
    }

    .stat-cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 20px;
        margin-bottom: 24px;

        .stat-card {
            border-radius: 16px;
            padding: 20px;
            text-align: center;

            .stat-value {
                font-size: 32px;
                font-weight: 700;
                color: #fff;
            }

            .stat-label {
                font-size: 14px;
                color: rgba(255, 255, 255, 0.9);
                margin-top: 8px;
            }
        }
    }

    .filter-bar {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        align-items: center;
        margin-bottom: 24px;
        padding: 16px;
        background: #f5f7fa;
        border-radius: 12px;

        // 让查询和重置按钮不换行
        .el-button {
            flex-shrink: 0;
        }
    }

    .record-info {
        .product-name {
            font-weight: 500;
            margin-bottom: 4px;
        }

        .branch-name {
            font-size: 12px;
            color: #8c8c8c;
            margin-bottom: 4px;
        }

        .reservation-time {
            font-size: 12px;
            color: #409eff;
        }
    }

    .user-info {
        .user-phone {
            font-size: 12px;
            color: #8c8c8c;
            margin-top: 4px;
        }
    }

    .remark {
        font-size: 12px;
        color: #8c8c8c;
        margin-top: 4px;
    }

    .pagination-wrapper {
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;
    }

    .detail-content {
        .detail-section {
            margin-bottom: 20px;

            h4 {
                font-size: 14px;
                font-weight: 600;
                margin-bottom: 12px;
                color: #1a1a2e;
            }

            .admin-remark {
                background: #f5f7fa;
                padding: 12px;
                border-radius: 8px;
                color: #666;
            }
        }
    }

    // 操作按钮样式优化
    .action-buttons {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        flex-wrap: wrap;

        .el-button {
            padding: 5px 8px;
        }

        .process-btn {
            .el-icon {
                margin-right: 2px;
            }
        }
    }
}

// 响应式适配
@media (max-width: 1200px) {
    .reservation-record {
        .filter-bar {
            .el-select, .el-date-picker, .el-input {
                width: 140px !important;
            }
        }
        
        .action-buttons {
            flex-direction: column;
            gap: 4px;
        }
    }
}

@media (max-width: 768px) {
    .reservation-record {
        .filter-bar {
            flex-direction: column;
            align-items: stretch;
            
            .el-select, .el-date-picker, .el-input, .el-button {
                width: 100% !important;
                margin-bottom: 8px;
            }
        }
    }
}
</style>